import React, { useState, useEffect } from 'react';
import { Typography, Box, List, ListItem, ListItemContent, Button, Sheet, CircularProgress, Alert, Input, Avatar, Modal, ModalDialog, ModalClose, FormControl, FormLabel } from '@mui/joy';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function WorkerPanel() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [commentInputs, setCommentInputs] = useState({});
  const [editProfile, setEditProfile] = useState(null);
  const [workerProfile, setWorkerProfile] = useState(null); // Added to store profile data

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('role');
    if (token && userRole) {
      setIsLoggedIn(true);
      setRole(userRole);
    } else {
      navigate('/signin');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      const userId = localStorage.getItem('id');

      console.log('WorkerPanel localStorage:', { token, role, userId });

      if (!token) {
        setError('No token found. Please sign in.');
        setLoading(false);
        navigate('/signin');
        return;
      }
      if (role !== 'worker') {
        setError('Access denied: You are not a worker.');
        setLoading(false);
        navigate(`/profile/${userId}`);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const [tasksResponse, profileResponse] = await Promise.all([
          axios.get('http://localhost:5000/api/worker/tasks', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`http://localhost:5000/api/profile/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        console.log('Tasks API response:', tasksResponse);
        console.log('Tasks data:', tasksResponse.data);
        console.log('Tasks field:', tasksResponse.data?.tasks);
        console.log('Profile response:', profileResponse.data);
        console.log('Profile image on load:', profileResponse.data.image);

        const fetchedTasks = Array.isArray(tasksResponse.data?.tasks) ? tasksResponse.data.tasks : [];
        setTasks(fetchedTasks);
        console.log('Tasks state set to:', fetchedTasks);

        if (fetchedTasks.length === 0) {
          setError(tasksResponse.data?.message || 'No tasks assigned to you.');
        }

        setProfileImage(profileResponse.data.image || '');
        setWorkerProfile({ // Store profile data
          id: profileResponse.data._id,
          name: profileResponse.data.name,
          country: profileResponse.data.country,
          skillTags: profileResponse.data.skillTags.join(', '),
        });
      } catch (err) {
        console.error('Fetch tasks error:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText,
        });
        setError(err.response?.data?.message || 'Failed to fetch tasks. Please try again.');
      } finally {
        setLoading(false);
        console.log('Loading state set to:', false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `http://localhost:5000/api/worker/task/${taskId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(tasks.map(task => (task._id === taskId ? response.data.task : task)));
      setError('');
      alert('Task status updated successfully');
    } catch (err) {
      console.error('Update task error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Error updating task status.');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('id');
      const response = await axios.post('http://localhost:5000/api/upload-image', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Image upload response:', response.data);
      setProfileImage(response.data.imageUrl);
      setProfileError('');
      const updatedProfileResponse = await axios.get(`http://localhost:5000/api/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfileImage(updatedProfileResponse.data.image || '');
      alert('Profile picture uploaded successfully');
    } catch (err) {
      console.error('Image upload error:', err.response?.data || err.message);
      setProfileError(err.response?.data?.message || 'Failed to upload image');
    }
  };

  const handleAddComment = async (taskId) => {
    const comment = commentInputs[taskId];
    if (!comment) {
      setError('Comment cannot be empty.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `http://localhost:5000/api/worker/task/${taskId}/comment`,
        { comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(tasks.map(task => (task._id === taskId ? response.data.task : task)));
      setCommentInputs({ ...commentInputs, [taskId]: '' });
      setError('');
      alert('Comment added successfully');
    } catch (err) {
      console.error('Add comment error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Error adding comment.');
    }
  };

  const handleUpdateProfile = async () => {
    if (!editProfile.name || !editProfile.country) {
      setError('Name and country are required.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('id');
      await axios.put('http://localhost:5000/api/update-profile', {
        userId: editProfile.id,
        name: editProfile.name,
        country: editProfile.country,
        skillTags: editProfile.skillTags.split(',').map(tag => tag.trim()),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkerProfile({
        id: editProfile.id,
        name: editProfile.name,
        country: editProfile.country,
        skillTags: editProfile.skillTags,
        image: editProfile.image 
      });
      setEditProfile(null);
      setError('');
      alert('Profile updated successfully');
    } catch (err) {
      console.error('Update profile error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Error updating profile.');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setRole('');
    navigate('/signin');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: 'sm',
        }}
      >
        <Typography level="h6">Mosaic Global Tech</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isLoggedIn ? (
            <>
              {role === 'admin' && (
                <Button onClick={() => navigate('/admin')} variant="plain" color="neutral">
                  Admin Panel
                </Button>
              )}
              {role === 'worker' && (
                <Button onClick={() => navigate('/worker')} variant="plain" color="neutral">
                  Worker Panel
                </Button>
              )}
              <Button onClick={handleLogout} color="danger">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => navigate('/')} variant="plain" color="neutral">
                Home
              </Button>
              <Button onClick={() => navigate('/register')} variant="plain" color="neutral">
                Sign Up
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Sheet
        sx={{
          maxWidth: 800,
          mx: 'auto',
          my: 4,
          p: 3,
          borderRadius: 'md',
          boxShadow: 'lg',
        }}
      >
        <Typography level="h3" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold', color: 'primary.main' }}>
          Worker Panel
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
            <Button size="sm" variant="outlined" color="neutral" sx={{ ml: 2 }} onClick={() => setError('')}>
              Clear
            </Button>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
              <Typography level="h5" sx={{ mb: 2 }}>Your Profile</Typography>
              {profileError && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {profileError}
                  <Button size="sm" variant="outlined" color="neutral" sx={{ ml: 2 }} onClick={() => setProfileError('')}>
                    Clear
                  </Button>
                </Alert>
              )}
              <Avatar
                src={profileImage ? `http://localhost:5000${profileImage}` : 'https://placehold.co/150x150'}
  sx={{ width: 100, height: 100, mb: 2, mx: 'auto' }}
  onError={() => console.log('Avatar load error:', profileImage)}
              />
              <Input
                type="file"
                onChange={handleImageUpload}
                sx={{ mb: 2 }}
                accept="image/*"
              />
              <Button
                color="neutral"
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => setEditProfile(workerProfile)} // Set editProfile only when button is clicked
              >
                Edit Profile
              </Button>
              {workerProfile && (
                <>
                  <Typography sx={{ mt: 2 }}>Name: {workerProfile.name}</Typography>
                  <Typography>Country: {workerProfile.country}</Typography>
                  <Typography>Skills: {workerProfile.skillTags}</Typography>
                </>
              )}
            </Box>

            <Typography level="h5" sx={{ mb: 2 }}>Your Tasks</Typography>
            <Box>
              {tasks.length > 0 ? (
                <List>
                  {tasks.map((task) => (
                    <ListItem key={task._id} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                      <ListItemContent>
                        <Typography level="h6">{task.title}</Typography>
                        <Typography>{task.description}</Typography>
                        <Typography level="body2">Status: {task.status}</Typography>
                        <Typography level="body2">
                          Assigned: {task.createdAt ? new Date(task.createdAt).toLocaleString() : 'N/A'}
                        </Typography>
                        {task.comments && task.comments.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography level="body2">Your Comments:</Typography>
                            <List>
                              {task.comments.map((comment, index) => (
                                <ListItem key={index}>
                                  <Typography level="body2">
                                    {comment.text} - {new Date(comment.createdAt).toLocaleString()}
                                  </Typography>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        <Box sx={{ mt: 2 }}>
                          <Typography level="body2">Add Comment</Typography>
                          <Input
                            placeholder="Enter your comment"
                            value={commentInputs[task._id] || ''}
                            onChange={(e) => setCommentInputs({ ...commentInputs, [task._id]: e.target.value })}
                            sx={{ mb: 1 }}
                          />
                          <Button
                            color="primary"
                            onClick={() => handleAddComment(task._id)}
                          >
                            Submit Comment
                          </Button>
                        </Box>
                      </ListItemContent>
                      {task.status === 'pending' && (
                        <Button
                          color="success"
                          onClick={() => handleUpdateStatus(task._id, 'completed')}
                        >
                          Mark as Completed
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography>No tasks assigned to you.</Typography>
              )}
            </Box>
          </>
        )}

        {editProfile && (
          <Modal open={!!editProfile} onClose={() => setEditProfile(null)}>
            <ModalDialog>
              <ModalClose />
              <Typography level="h5" sx={{ mb: 2 }}>Edit Your Profile</Typography>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editProfile.name}
                  onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Country</FormLabel>
                <Input
                  value={editProfile.country}
                  onChange={(e) => setEditProfile({ ...editProfile, country: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Skill Tags (comma-separated)</FormLabel>
                <Input
                  value={editProfile.skillTags}
                  onChange={(e) => setEditProfile({ ...editProfile, skillTags: e.target.value })}
                />
              </FormControl>
              <Button color="primary" onClick={handleUpdateProfile}>
                Save Changes
              </Button>
            </ModalDialog>
          </Modal>
        )}
      </Sheet>
    </>
  );
}